export * from './first-party-mcp-connectors';
export * from './compliance-coverage';
export * from './google-calendar-connect';
export * from './alfred-forwarding';
export const SCHEDULING_ASSISTANT_USER_APP_DEF_ID = '4886d214-d965-4e97-9f49-51c7a80a345f';
export const SCHEDULING_ASSISTANT_UOU_APP_DEF_ID = 'f5b4ce00-4f7b-4066-a607-0277d16780b8';

// DevCenter component (type=WIX_AI_GATEWAY_CALLBACK) that routes WixAiGateway async
// completions back to orion-harness-poc via the SPI HandleResult callback. The appId
// is SCHEDULING_ASSISTANT_UOU_APP_DEF_ID above.
export const ORION_HARNESS_WIX_AI_GATEWAY_CALLBACK_COMPONENT_ID = '34b09d1f-1e9c-4a9a-8084-aef76c3ecb98';
export const WIX_WHAAAT_METASITE_ID = 'cc119d2c-2d63-4cf0-9531-b07ef0728c91';
export const WIX_WELLNESS_METASITE_ID = 'b1e78495-753d-4301-963d-2a6dec50fb8f';
export const BOOKINGS_APP_DEF_ID = '13d21c63-b5ec-5912-8397-c3a5ddb27a97';
export const ALFRED_APP_DEF_ID = 'fc0ee985-24a5-4f3f-91c8-704b46e93550';
export const BM_STANDALONE_APP_BASE_URL = 'https://manage.wix.com/orion';
export const BM_CONNECT_CALENDAR_SUCCESS_PAGE_URL = `${BM_STANDALONE_APP_BASE_URL}/connect/success`;
export const BM_CONNECT_CALENDAR_FAILURE_PAGE_URL = `${BM_STANDALONE_APP_BASE_URL}/connect/failure`;
export const SYMPHONY_WEB_BASE_URL = 'https://symphony.wix.com';
// The mobile app's universal-link host: an `https://symphony.wix.app/…` URL opens the corresponding app
// screen when tapped in a chat (the app derives its internal `orion://` scheme from it at render time).
// See the deep-link table in skills/app-guide.ts, which pins the same host as part of the contract.
export const SYMPHONY_APP_BASE_URL = 'https://symphony.wix.app';
/**
 * The two spellings ONE entity's detail has: the app's universal-link path, and orion-web's own route.
 * They differ — web has no routine-detail screen, so a routine opens as an expanded card on the list — and
 * `entityDeeplink` carries BOTH in a single URL rather than picking one per surface.
 *
 * The `web` values are pinned against orion-web's resolver, its page files and its redirect table by
 * `orion-web/test/deeplink-surfaces.test.ts`, so a route rename there fails a test rather than silently
 * minting a link that opens the wrong thing.
 */
export const DEEPLINK_PATHS = {
  task: {
    app: 'task/{id}',
    web: 'tasks/{id}'
  },
  routine: {
    app: 'pulse/{id}',
    web: 'pulses?openId={id}'
  }
};
/**
 * The surface-specific path (no leading slash) for one entity, with `id` substituted and encoded.
 *
 * The encoding holds within OUR two consumers — web's renderer resolves the pathname itself, and an id
 * carrying `&` would otherwise split the query it lands in. It does NOT survive the links service: `d` is
 * decoded twice there, so `openId=a%2526b` arrives as `openId=a&b` and the id truncates (measured against
 * `symphony.wix.app` per user-agent). Ids are server-assigned GUIDs, so nothing live reaches that; the
 * encoding is here so a malformed id degrades to a wrong-id link rather than a broken URL, not because a
 * reserved character round-trips.
 */
export function deeplinkPath(entity, surface, id) {
  return DEEPLINK_PATHS[entity][surface].replace('{id}', encodeURIComponent(id));
}

/**
 * ONE link to an entity, correct wherever it is opened — no per-surface choice, because the surface a
 * conversation happens on is not the surface the link gets opened on. A chat is visible on both a desktop
 * and a phone, and a link gets copied, pasted and reopened long after it was sent.
 *
 * The links service in front of `symphony.wix.app` resolves it per user-agent, and BOTH parameters are
 * required to get that right (measured per user-agent, not reasoned about):
 *
 * - `d` is the desktop destination, but the service applies it on EVERY platform
 *   (`mobileUrl.filter(isMobileOS).orElse(desktopUrl)`), so on its own it would capture phones too and
 *   send them to the web instead of the app.
 * - `s` (`suggestMobile`) gates the store-link resolver that runs ahead of `d`
 *   (`if (suggestMobile && isMobileOS(userAgent))`), which is what keeps `d` desktop-only.
 *
 * So: a phone with the app opens the app on the entity; a phone without it goes to the store with the
 * deferred `?deeplink=` intact; a desktop lands on the web page. Inside web chat the renderer reads the
 * pathname and ignores the query, so a click is an in-app navigation rather than a page load.
 *
 * The same trap is written up in `native-apps/GOTCHAS.md`: a link change must be tested with a MOBILE
 * user-agent and the app uninstalled, because the installed-app path keeps working and hides the failure.
 *
 * And its corollary: anything that must survive the store — including attribution — belongs in
 * `appPath`, never in the query. The service rewrites a phone's request to `…/time?deeplink=<appPath>`
 * and drops every query parameter, so a `utm_*` tag on the link is lost for exactly the users who had to
 * install first. Pack invites carry theirs as positional path segments after the token; see the
 * "pack-invite ATTRIBUTION has to be a path too" gotcha.
 *
 * The same two parameters are hand-built in four other places (both native referral builders, orion-web's
 * `referral-share-link.ts` and the server's native-data endpoint). New producers should call this rather
 * than copy the shape a sixth time.
 */
export function appLinkWithDesktopFallback(appPath, webPath) {
  const web = `${SYMPHONY_WEB_BASE_URL}/${webPath}`;
  return `${SYMPHONY_APP_BASE_URL}/${appPath}?s=true&d=${encodeURIComponent(web)}`;
}

/**
 * The one address that reaches the calendar-connect screen from either surface.
 *
 * The two do not share a route: the app maps `settings/calendars` to the calendar row, and web has no
 * such page at all — its screen is under `settings/connectors/tools/google-calendar`. So the app path
 * carries the web one as its `d` fallback and the links service resolves per user-agent.
 *
 * Rendered finished rather than described, because a model asked to build one of these gets it wrong
 * in a way the user absorbs: a bare `symphony.wix.app/...` with no `s`/`d` is not a 404 but the app
 * INSTALL page, which reads as a working link to everyone except the person who tapped it. Inside the
 * product's own chat both forms resolve the same, because that renderer reads the path and ignores the
 * query; the difference is a link opened anywhere else.
 */
export const RECONNECT_CALENDAR_LINK = appLinkWithDesktopFallback('settings/calendars', 'settings/connectors/tools/google-calendar');
export function entityDeeplink(entity, id) {
  return appLinkWithDesktopFallback(deeplinkPath(entity, 'app', id), deeplinkPath(entity, 'web', id));
}
export const INDIVIDUAL_CONNECT_CALENDAR_SUCCESS_PAGE_URL = `${SYMPHONY_WEB_BASE_URL}/connect/success`;
export const INDIVIDUAL_CONNECT_CALENDAR_FAILURE_PAGE_URL = `${SYMPHONY_WEB_BASE_URL}/connect/failure`;
// Where Nylas returns a hosted calendar connect, registered on the Nylas app as its redirect URI. The page
// completes nothing itself: it hands the code to the signed-in web app, or to the phone app, that started it.
export const CALENDAR_OAUTH_RETURN_URL = `${SYMPHONY_WEB_BASE_URL}/calendar-oauth-return`;
// Brand identity axes, kept separate on purpose (see resolveAgentIdentity): the PRODUCT/app name
// ("Symphony") and the primary AGENT's name ("The Maestro") were one word ("Orion") and split at the
// Symphony rebrand. The rebrand has GA'd and the `symphonyRebrand` Petri experiment was removed, so these
// resolve to the Symphony-era values unconditionally. A per-user override of the agent name lives in the
// Recipient entity (agentName field).
export const MAESTRO_ASSISTANT_NAME = 'The Maestro';
// Female presentation of the primary agent's name. The Maestro is the only assistant name that genders
// (The Maestro → The Maestra); the persona archetypes (Guide/Chief/Maverick) are gender-neutral.
export const MAESTRA_ASSISTANT_NAME = 'The Maestra';
export const SYMPHONY_PRODUCT_NAME = 'Symphony';
export const STATIC_IMAGE_DOMAIN = 'https://static.wixstatic.com';
export const UOU_CONVERSATION_ORIGIN = 'uou-scheduling-assistant';
export const INDIVIDUAL_CONVERSATION_ORIGIN = 'individual-scheduling-assistant';
export const MEETING_INVITATION_SENT_AUTOMATION_ID = '707be516-5a63-435a-b020-53b8e5542eec';
export const MEETING_REMINDERS_AUTOMATION_ID = '707be516-5a63-435a-b020-53b8e5542eec';
export const UOU_UNIFIED_ASSISTANT_ID = '816d02ea-0c56-4fe7-8850-fc420383a249';
export const INDIVIDUAL_UNIFIED_ASSISTANT_ID = '5df736a5-c235-4648-8eb1-df868689c9e4';
export const INDIVIDUAL_ONBOARDING_UNIFIED_ASSISTANT_ID = 'cb4b460a-b2cb-41e7-8da1-59f89c07d5fd';
export const INDIVIDUAL_KNOWLEDGE_UNIFIED_ASSISTANT_ID = 'e3af2dde-ae3a-4d5e-bb71-6a17631ac5f7';
export const UOU_SCHEDULING_ASSISTANT_HEADLESS_CLIENT_ID = '76e857b7-b04e-42b6-9e03-dbc1e7bc7b40';
export const ASSISTANT_NAME_CHAR_LIMIT = 15;
/**
 * The "send to the model vs. carry as a URL" threshold. NOT an upload limit.
 *
 * A file at or under it is handled by type (images and PDFs go to the model as media, Office files and
 * zips are converted, audio is transcribed). A file over it is uploaded all the same and treated like a
 * video: the harness never reads or sends its bytes and resolves it to a note with its name, type, size
 * and URL, which the agent or a sub-agent can fetch. 5 MB is Anthropic's per-image bound.
 *
 * History: #4274 (May 2026) used this as a refusal for every type. That turned away every file over
 * 5 MB; it is now only this threshold. Clients still downscale photos under it, so the model sees them.
 */
export const MAX_MODEL_INLINE_ATTACHMENT_BYTES = 5 * 1024 * 1024;

/** @deprecated The old name of `MAX_MODEL_INLINE_ATTACHMENT_BYTES`; it is no longer an upload limit. */
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_MODEL_INLINE_ATTACHMENT_BYTES;

/**
 * Video containers a user may attach. Explicit rather than a `video/*` prefix test: these are the three
 * the destinations actually take (and the three `rehost-reply-assets` already maps), so an `.avi` fails
 * at the picker with a clear reason instead of at the post, after an upload the user waited for.
 */
export const VIDEO_ATTACHMENT_MIMES = ['video/mp4', 'video/quicktime', 'video/webm'];

/** True for a video container we accept. Tolerant of a `;codecs=…` parameter, which pickers do append. */
export function isVideoAttachmentMime(mime) {
  return VIDEO_ATTACHMENT_MIMES.includes((mime ?? '').toLowerCase().split(';')[0].trim());
}

/**
 * There is no upload size limit on our side, for any type. The only limits left are the platforms':
 * WixMP's own per-file limits at the mint and PUT, Meta's media cap for WhatsApp, and Composio's
 * staging cap (`MAX_STAGED_BYTES`) when a file is posted through a connector that needs the bytes.
 *
 * True when a file is small enough for the harness to read or send to the model by its type.
 */
export function isModelInlineAttachmentSize(size) {
  return size === undefined || size <= MAX_MODEL_INLINE_ATTACHMENT_BYTES;
}

/**
 * The name and mime to hand WixMP for a file, which are not always the file's own.
 *
 * WixMP stores exactly one text type, `text/plain`. Every other `text/*` subtype is refused at the mint
 * with `Requested file type is unrecognized or is not supported`, so `text/markdown`, `text/csv` and
 * `text/html` all have to go up as plain text.
 *
 * **The name and the mime move TOGETHER, and that is the whole point.** WixMP validates one against the
 * other, so renaming `catalog.csv` → `catalog.csv.txt` while still declaring `text/csv` is refused with
 * `MimeType is not matching the file extension`: the half-applied fallback causes the failure it was
 * meant to prevent, and that is exactly how every CSV/markdown/HTML upload from the web once broke.
 *
 * Whatever the caller shows the user keeps the original name; only the stored object is `.txt`.
 *
 * **Every other type is accepted too, so the same trick covers the rest.** WixMP types a file by the
 * extension of this name and answers `UNSUPPORTED_FILE_FORMAT` or "unrecognized" for one it does not know,
 * and it publishes no list of what it knows. So a type is passed through under its own name only when it is
 * one we have seen WixMP store (`WIXMP_NATIVE_MIME` below). Anything else goes up under a type it does
 * store: text-like files (source code, JSON, subtitles, RTF) as `.txt`, and every other binary (`.psd`,
 * `.ai`, `.blend`, a bare `application/octet-stream`) as `.zip` with `application/zip`. WixMP stores the
 * bytes and does not read them, and the persisted attachment keeps the user's own name and mime, so the
 * harness still sees what the file really is. A type proven to work under its own name can join the list.
 *
 * This is the one copy. It was learned separately, and at least once painfully, on four surfaces
 * (`orion-web/pages/api/upload-url.ts`, iOS `wixmpSafeTextUploadIdentity`, RN `ensureFileNameExtension`,
 * and the WhatsApp inbound path), while the two that never got it — the shared media mint and the
 * sandbox export RPC — are the two where these uploads still fail. Call this rather than re-deriving it.
 */
export function wixmpSafeUploadIdentity(fileName, mimeType) {
  const name = (fileName ?? '').trim();
  const mime = (mimeType ?? '').toLowerCase().split(';')[0].trim();
  // The mime is NEVER rewritten on this branch: forcing `text/plain` onto an unnamed JPEG would mint a
  // `file.txt` holding image bytes.
  if (WIXMP_NATIVE_MIME.test(mime)) {
    return {
      fileName: name || 'file',
      mimeType
    };
  }
  if (mime.startsWith('text/') || TEXT_LIKE_MIME.test(mime) || isOpaqueMime(mime) && TEXT_LIKE_EXTENSION.test(name)) {
    return {
      fileName: !name ? 'file.txt' : /\.txt$/i.test(name) ? name : `${name}.txt`,
      mimeType: 'text/plain'
    };
  }
  return {
    fileName: !name ? 'file.zip' : /\.zip$/i.test(name) ? name : `${name}.zip`,
    mimeType: 'application/zip'
  };
}

/**
 * Types WixMP has stored under their own name for us in production: images, audio (voice notes), the
 * three video containers, PDF, the Office formats, zip and plain text. Kept narrow on purpose: a type is
 * on it because an upload worked, not because a list said it might.
 */
const WIXMP_NATIVE_MIME = new RegExp('^(' + ['image/(jpeg|jpg|png|gif|webp|bmp|tiff|heic|heif|avif|svg\\+xml)', 'audio/.+', 'video/(mp4|quicktime|webm)', 'application/pdf', 'application/zip', 'application/x-zip-compressed', 'application/msword', 'application/vnd\\.ms-excel', 'application/vnd\\.ms-powerpoint', 'application/vnd\\.openxmlformats-officedocument\\.(spreadsheetml\\.sheet|wordprocessingml\\.document|presentationml\\.presentation)', 'text/plain'].join('|') + ')$');

/** Non-`text/*` mimes whose bytes are text, so storing them as `.txt` keeps them readable when opened. */
const TEXT_LIKE_MIME = /^application\/(json|xml|javascript|ecmascript|x-javascript|typescript|x-typescript|x-yaml|yaml|x-sh|x-python|x-python-code|sql|x-sql|graphql|rtf|x-tex|x-httpd-php|x-ndjson|[a-z0-9.+-]+\+(json|xml))$/;

/** Pickers often send source files as `application/octet-stream` or no mime at all; the name says what they are. */
const TEXT_LIKE_EXTENSION = /\.(txt|md|markdown|csv|tsv|json|jsonl|ndjson|xml|ya?ml|toml|ini|cfg|conf|env|log|vtt|srt|sbv|ass|ics|vcf|rtf|tex|html?|css|scss|less|js|mjs|cjs|jsx|ts|tsx|py|rb|go|rs|java|kt|kts|swift|scala|c|cc|cpp|h|hpp|cs|php|pl|lua|r|sql|sh|bash|zsh|ps1|bat|graphql|gql|proto|dockerfile|gradle)$/i;
function isOpaqueMime(mime) {
  return mime === '' || mime === 'application/octet-stream' || mime === 'binary/octet-stream';
}
export const SEND_MESSAGE_TIME_BUDGET = 360_000;
export const ABORT_MESSAGE_TIME_BUDGET = 30_000;
// The mailbox we hand out and receive on. email-integration watches two Gmail mailboxes and this is
// its `defaultOutbound` one — the From address on every new thread, so it is also the address a
// recipient replies to. `orion@wix.com` is still configured there as the legacy mailbox (it owns all
// pre-rename threads and their per-mailbox Gmail threadIds) but must never be advertised again.
export const SYMPHONY_MAILBOX_ADDRESS = 'symphony@wix.com';
export const SYMPHONY_WHATSAPP_WABA_ID = '742949560008212';
export const SYMPHONY_WHATSAPP_PHONE_NUMBER_ID = '753186627877106';
export const SYMPHONY_WHATSAPP_INBOUND_NUMBER = '15188556881';
export const SYMPHONY_SMS_ACCOUNT_ID = '00000000-0000-0000-0000-000000000001';
export const SYMPHONY_SMS_INBOUND_NUMBER = '+18888953379';
// The SMS provider's hard ceiling on a message body: `SmsProviderService/SendSms` rejects anything
// longer with `INVALID_ARGUMENT: body has size N, expected 1530 or less`, and the send simply never
// happens. Enforced at the SmsClient boundary (the one place every send funnels through) rather than
// per-call-site, so the intro template, the opt-in/opt-out confirmations and the assistant's own
// replies are all covered by construction.
export const SMS_BODY_MAX_CHARS = 1530;
// The seeded, non-deletable "daily agent meeting" (morning brief) PulseIdentity. It is a SITE-LEVEL
// routine with an empty workspaceId, so a workspace-scoped routines query never matches it — consumers
// that want it in a workspace list must merge it in by this id. Same value as orion-web's
// lib/brief-state.ts BRIEF_PULSE_IDENTITY_ID and orion-live-server's DAILY_MEETING_PULSE_IDENTITY_ID.
export const BRIEF_PULSE_IDENTITY_ID = '00000000-0000-0000-0000-000000000002';

// Browserbase constants
export const BROWSERBASE_CONSTANTS = {
  STAGEHAND: {
    ENV: 'BROWSERBASE',
    MODEL_NAME: 'claude-3-7-sonnet-latest',
    HEADLESS: true,
    ENABLE_CACHING: true
  },
  SCREENSHOT: {
    TYPE: 'png',
    ENCODING: 'base64'
  }
};
export const FEEDBACK_FORM_ID = '910cc899-42f8-4810-81c9-3681ad488bc8';
export const LANDING_PAGE_WEBSITE_MSID = '70ab3653-f410-455f-a7f6-0e39c48963ab';

// Default categories for task digest
export const DEFAULT_CATEGORIES = ['Work', 'Errands', 'Family', 'Health', 'Social', 'Travel', 'Events & Culture', 'Finance', 'Learning & Enrichment'];

/**
 * The languages Symphony supports, as bare ISO-639-1 codes (the shape Site Properties stores).
 *
 * This is the same 37-locale catalog every client surface already ships: `SUPPORTED_LOCALES` in
 * orion-web's `lib/i18n.ts`, `Loc.supported` in the iOS and Android apps, and the `messages_<code>.json`
 * bundles in ai-scheduling-assistant-localization. Those copies can't be shared — orion-web is a private
 * Next.js app with no exports field, and the native lists are Swift/Kotlin — so this is the importable
 * one for the server-side packages. Adding or dropping a locale means updating the client lists too.
 */
export const SUPPORTED_LANGUAGE_CODES = ['ar', 'bg', 'ca', 'cs', 'da', 'de', 'el', 'en', 'es', 'fi', 'fr', 'he', 'hi', 'hr', 'hu', 'id', 'it', 'ja', 'ko', 'lt', 'lv', 'ms', 'nl', 'no', 'pl', 'pt', 'ro', 'ru', 'sk', 'sl', 'sv', 'th', 'tl', 'tr', 'uk', 'vi', 'zh'];
export function isSupportedLanguageCode(code) {
  return SUPPORTED_LANGUAGE_CODES.includes(code);
}